import React, { useEffect, useState } from "react";
import { Title, Text, Group, Button, LoadingOverlay, Alert, Stack, Paper, Badge, Tabs, Divider, Grid, Card, ActionIcon } from "@mantine/core";
import { MdArrowBack, MdRefresh } from "react-icons/md";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { notifications } from "@mantine/notifications";

import { getToken, useLogin } from "../../../authConfig";
import { getUserDetailsApi, UserDetailsApiResponse } from "../../../api";
import { FeedbackTable } from "../../../components/feedback-table/FeedbackTable";
import { HistoryTable } from "../../../components/history-table";
import { ConversationsChart } from "../../../components/charts/ConversationsChart";
import { FeedbackChart } from "../../../components/charts/FeedbackChart";
import { ChatMessagesChart } from "../../../components/charts/ChatMessagesChart";
import { PeakHoursChart } from "../../../components/charts/PeakHoursChart";
import { AverageChatMessagesChart } from "../../../components/charts/AverageChatMessagesChart";
import { TouchpointChart } from "../../../components/charts/TouchpointChart";

interface UserDetailsProps {
    userId: string;
}

const UserDetailsContent: React.FC<UserDetailsProps> = ({ userId }) => {
    const navigate = useNavigate();
    const { instance } = useMsal();
    const [searchParams, setSearchParams] = useSearchParams();

    const [user, setUser] = useState<UserDetailsApiResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const resolveTab = (value?: string | null) => {
        if (value === "feedback") {
            return "feedback";
        }
        if (value === "history") {
            return "history";
        }
        if (value === "insights") {
            return "insights";
        }
        return "overview";
    };
    const [activeTab, setActiveTab] = useState<string>(() => resolveTab(searchParams.get("tab")));

    const updateSearchParams = (updates: Record<string, string | null>) => {
        const newParams = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => {
            if (!value) {
                newParams.delete(key);
            } else {
                newParams.set(key, value);
            }
        });
        setSearchParams(newParams);
    };

    const handleTabChange = (value: string | null) => {
        const nextTab = resolveTab(value);
        setActiveTab(nextTab);
        updateSearchParams({ tab: nextTab });
    };

    const loadUser = async () => {
        if (!useLogin || !userId) return;

        setLoading(true);
        setError(null);

        try {
            const token = await getToken(instance);
            if (!token) {
                throw new Error("No idToken available for fetching user details");
            }
            const userIdStr = userId;
            if (!userIdStr) {
                throw new Error("User ID is required");
            }
            const userData = await getUserDetailsApi(token, userId);
            setUser(userData);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load user details");
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (timestamp: number | null): string => {
        if (!timestamp) return "Never";
        return new Date(timestamp * 1000).toLocaleString();
    };

    const formatISODate = (isoString: string | null): string => {
        if (!isoString) return "Never";
        return new Date(isoString).toLocaleString();
    };

    useEffect(() => {
        loadUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    if (!userId) {
        return (
            <Alert color="red" title="Error">
                User ID is required
            </Alert>
        );
    }

    return (
        <Stack gap="md" pos="relative">
            <LoadingOverlay visible={loading} />

            <Stack gap="md">
                <Group justify="space-between" align="center">
                    <Group>
                        <ActionIcon variant="light" onClick={() => navigate(`/administration/users/directory?${searchParams.toString()}`)} size="lg">
                            <MdArrowBack size={18} />
                        </ActionIcon>
                        <div>
                            <Title order={3}>{user?.name || "User Details"}</Title>
                            <Text size="sm" c="dimmed">
                                {user?.username || "N/A"}
                            </Text>
                        </div>
                    </Group>
                    <Group gap="xs">
                        <Button variant="light" leftSection={<MdRefresh size={16} />} onClick={loadUser} disabled={loading}>
                            Refresh
                        </Button>
                    </Group>
                </Group>

                {error && <Alert color="red">{error}</Alert>}

                {user && (
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="pills"
                        radius="md"
                        style={{ flex: 1, display: "flex", flexDirection: "column" }}
                    >
                        <Tabs.List>
                            <Tabs.Tab value="overview">Overview</Tabs.Tab>
                            <Tabs.Tab value="feedback">Feedback</Tabs.Tab>
                            <Tabs.Tab value="history">History</Tabs.Tab>
                            <Tabs.Tab value="insights">Insights</Tabs.Tab>
                        </Tabs.List>

                        <Tabs.Panel value="overview" style={{ flex: 1, display: "flex", flexDirection: "column" }} py="md">
                            <Paper p="md">
                                <Grid gap="lg">
                                    <Grid.Col span={{ base: 12, md: 6 }}>
                                        <Card withBorder radius="lg" shadow="sm">
                                            <Stack gap="sm">
                                                <Title order={5}>Account Information</Title>
                                                <Divider my="sm" />

                                                <Stack gap="xs">
                                                    <Text fw={500}>Name</Text>
                                                    <Text size="sm" c="dimmed">
                                                        {user.name}
                                                    </Text>
                                                </Stack>

                                                <Stack gap="xs">
                                                    <Text fw={500}>Username</Text>
                                                    <Text size="sm" c="dimmed">
                                                        {user.username || "N/A"}
                                                    </Text>
                                                </Stack>

                                                <Stack gap="xs">
                                                    <Text fw={500}>Entra OID</Text>
                                                    <Text size="sm" c="dimmed">
                                                        {user.entra_oid || "N/A"}
                                                    </Text>
                                                </Stack>

                                                <Stack gap="xs">
                                                    <Text fw={500}>Last Activity</Text>
                                                    <Text size="sm" c="dimmed">
                                                        {formatISODate(user.activity_stats?.last_activity ?? null)}
                                                    </Text>
                                                </Stack>
                                            </Stack>
                                        </Card>
                                    </Grid.Col>

                                    <Grid.Col span={{ base: 12, md: 6 }}>
                                        <Card withBorder radius="lg" shadow="sm">
                                            <Stack gap="sm">
                                                <Title order={5}>Activity Summary</Title>
                                                <Divider my="sm" />

                                                <Group gap="md">
                                                    <Stack gap={4} align="center">
                                                        <Badge color="blue" size="lg">
                                                            {user.activity_stats?.total_messages ?? 0}
                                                        </Badge>
                                                        <Text size="sm">Messages</Text>
                                                    </Stack>
                                                    <Stack gap={4} align="center">
                                                        <Badge color="green" size="lg">
                                                            {user.activity_stats?.total_conversations ?? 0}
                                                        </Badge>
                                                        <Text size="sm">Conversations</Text>
                                                    </Stack>
                                                </Group>

                                                <Divider my="sm" />

                                                <Group gap="lg" align="flex-start">
                                                    <Stack gap="xs">
                                                        <Text fw={500}>First Activity</Text>
                                                        <Text size="sm" c="dimmed">
                                                            {formatISODate(user.activity_stats?.first_activity ?? null)}
                                                        </Text>
                                                    </Stack>

                                                    <Stack gap="xs">
                                                        <Text fw={500}>Last Activity</Text>
                                                        <Text size="sm" c="dimmed">
                                                            {formatISODate(user.activity_stats?.last_activity ?? null)}
                                                        </Text>
                                                    </Stack>
                                                </Group>
                                            </Stack>
                                        </Card>
                                    </Grid.Col>
                                </Grid>
                            </Paper>
                        </Tabs.Panel>

                        <Tabs.Panel value="feedback" style={{ flex: 1, display: "flex", flexDirection: "column" }} py="md">
                            <FeedbackTable
                                userId={user.entra_oid}
                                title="User Feedback"
                                compact={false}
                                showExport={true}
                                showSearch={true}
                                showDateFilter={true}
                                maxHeight="60vh"
                            />
                        </Tabs.Panel>

                        <Tabs.Panel value="history" style={{ flex: 1, display: "flex", flexDirection: "column" }} py="md">
                            <HistoryTable
                                userId={user.entra_oid}
                                title="User Conversation History"
                                subtitle="View all conversations for this user"
                                compact={false}
                                showExport={true}
                                showSearch={true}
                                showDateFilter={true}
                                maxHeight="60vh"
                                hideUserColumn={true}
                            />
                        </Tabs.Panel>

                        <Tabs.Panel value="insights" style={{ flex: 1, display: "flex", flexDirection: "column" }} py="md">
                            <Stack gap="lg">
                                <Grid gap="lg">
                                    <Grid.Col span={{ base: 12, lg: 6 }}>
                                        <ConversationsChart
                                            title="CONVERSATIONS OVER TIME"
                                            height={300}
                                            showTitle={true}
                                            showLegend={true}
                                            showStats={true}
                                            entraOid={user.entra_oid}
                                            defaultAggregation="day"
                                            hideUniqueUsers={true}
                                            defaultDaysBack={14}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, lg: 6 }}>
                                        <FeedbackChart
                                            title="USER FEEDBACK"
                                            height={300}
                                            showTitle={true}
                                            showLegend={true}
                                            showStats={true}
                                            entraOid={user.entra_oid}
                                            defaultAggregation="day"
                                            defaultDaysBack={14}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, lg: 6 }}>
                                        <ChatMessagesChart
                                            title="TOTAL MESSAGES"
                                            height={300}
                                            showTitle={true}
                                            showLegend={true}
                                            showStats={true}
                                            entraOid={user.entra_oid}
                                            defaultAggregation="day"
                                            defaultDaysBack={14}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, lg: 6 }}>
                                        <AverageChatMessagesChart
                                            title="MESSAGES"
                                            height={300}
                                            showTitle={true}
                                            showLegend={true}
                                            showStats={true}
                                            entraOid={user.entra_oid}
                                            defaultAggregation="day"
                                            defaultDaysBack={14}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, lg: 6 }}>
                                        <PeakHoursChart
                                            title="ACTIVITY BY HOUR"
                                            height={300}
                                            showTitle={true}
                                            showLegend={true}
                                            showStats={true}
                                            entraOid={user.entra_oid}
                                            defaultDaysBack={14}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={{ base: 12, lg: 6 }}>
                                        <TouchpointChart
                                            title="TOUCHPOINT DISTRIBUTION"
                                            height={300}
                                            showTitle={true}
                                            showLegend={true}
                                            showStats={true}
                                            entraOid={user.entra_oid}
                                            defaultDaysBack={14}
                                        />
                                    </Grid.Col>
                                </Grid>
                            </Stack>
                        </Tabs.Panel>
                    </Tabs>
                )}
            </Stack>
        </Stack>
    );
};

export const UserDetailsPage: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();

    if (!userId) {
        return (
            <Paper p="md">
                <Alert color="red" title="Error">
                    User ID is required to view user details.
                </Alert>
            </Paper>
        );
    }

    return <UserDetailsContent userId={userId} />;
};
